/**
 * MedicationReminderDialog Component
 * 
 * Allows users to set up daily reminders for their medications.
 * Supports multiple times per day with preset schedules.
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Bell, BellOff, Plus, X, Clock, Pill, Check } from 'lucide-react';
import { toast } from 'sonner';
import medicationReminderService, { REMINDER_PRESETS } from '@/services/medicationReminderService';

// Preset schedule options
const PRESET_OPTIONS = [
  { value: 'ONCE_DAILY_MORNING', label: 'Once daily (Morning)', times: REMINDER_PRESETS.ONCE_DAILY_MORNING },
  { value: 'ONCE_DAILY_EVENING', label: 'Once daily (Evening)', times: REMINDER_PRESETS.ONCE_DAILY_EVENING },
  { value: 'TWICE_DAILY', label: 'Twice daily', times: REMINDER_PRESETS.TWICE_DAILY },
  { value: 'THREE_TIMES_DAILY', label: 'Three times daily', times: REMINDER_PRESETS.THREE_TIMES_DAILY },
  { value: 'FOUR_TIMES_DAILY', label: 'Four times daily', times: REMINDER_PRESETS.FOUR_TIMES_DAILY },
  { value: 'WITH_MEALS', label: 'With meals', times: REMINDER_PRESETS.WITH_MEALS },
  { value: 'BEFORE_BED', label: 'Before bed', times: REMINDER_PRESETS.BEFORE_BED },
  { value: 'CUSTOM', label: 'Custom times...', times: [] },
];

const MedicationReminderDialog = ({
  isOpen,
  onClose,
  medication,
  onSave,
}) => {
  const [remindersEnabled, setRemindersEnabled] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState('');
  const [customTimes, setCustomTimes] = useState([]);
  const [newTime, setNewTime] = useState('08:00');
  const [saving, setSaving] = useState(false);

  // Initialize from medication data
  useEffect(() => {
    if (medication) {
      setRemindersEnabled(medication.reminders_enabled || false);
      const times = medication.reminder_times || [];
      setCustomTimes(times);
      
      // Try to match to a preset
      const matchingPreset = PRESET_OPTIONS.find(p => 
        p.times.length === times.length && 
        p.times.every(t => times.includes(t))
      );
      setSelectedPreset(matchingPreset?.value || (times.length > 0 ? 'CUSTOM' : ''));
    }
  }, [medication]);

  // Handle preset selection
  const handlePresetChange = (value) => {
    setSelectedPreset(value);
    
    if (value === 'CUSTOM') {
      // Keep current custom times
      return;
    }
    
    const preset = PRESET_OPTIONS.find(p => p.value === value);
    if (preset) {
      setCustomTimes([...preset.times]);
    }
  };

  // Add custom time
  const addCustomTime = () => {
    if (!newTime) return;
    
    if (customTimes.includes(newTime)) {
      toast.error('This time is already added');
      return;
    }
    
    const updated = [...customTimes, newTime].sort();
    setCustomTimes(updated);
    setSelectedPreset('CUSTOM');
  };

  // Remove a time
  const removeTime = (time) => {
    setCustomTimes(customTimes.filter(t => t !== time));
    setSelectedPreset('CUSTOM');
  };

  // Format time for display
  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Save reminders
  const handleSave = async () => {
    setSaving(true);
    
    try {
      // Update medication with reminder settings
      const updatedMedication = {
        ...medication,
        reminders_enabled: remindersEnabled,
        reminder_times: remindersEnabled ? customTimes : [],
      };
      
      // Schedule or cancel notifications
      if (remindersEnabled && customTimes.length > 0) {
        const result = await medicationReminderService.scheduleMedicationReminder({
          id: medication.id,
          name: medication.name,
          dosage: medication.dosage,
          reminderTimes: customTimes,
          instructions: medication.instructions,
        });
        
        if (!result.success) {
          toast.error('Could not schedule reminders', {
            description: result.error,
          });
          return;
        }
        
        toast.success('Reminders scheduled!', {
          description: `You'll be reminded at ${customTimes.map(formatTime).join(', ')}`,
        });
      } else {
        await medicationReminderService.cancelMedicationReminders(medication.id);
        
        if (!remindersEnabled) {
          toast.success('Reminders turned off');
        }
      }
      
      // Save to backend
      await onSave(updatedMedication);
      onClose();
    } catch (error) {
      console.error('Failed to save reminders:', error);
      toast.error('Failed to save reminder settings');
    } finally {
      setSaving(false);
    }
  };

  if (!medication) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pill className="h-5 w-5 text-blue-500" />
            Medication Reminders
          </DialogTitle>
          <DialogDescription>
            Set up daily reminders for {medication.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              {remindersEnabled ? (
                <Bell className="h-5 w-5 text-blue-500" />
              ) : (
                <BellOff className="h-5 w-5 text-slate-400" />
              )}
              <div>
                <Label className="text-base font-medium">
                  Daily Reminders
                </Label>
                <p className="text-sm text-slate-500">
                  Get notified when it&apos;s time to take this medication
                </p>
              </div>
            </div>
            <Switch
              checked={remindersEnabled}
              onCheckedChange={setRemindersEnabled}
              data-testid="reminder-toggle"
            />
          </div>

          {remindersEnabled && (
            <>
              {/* Preset Schedule */}
              <div className="space-y-2">
                <Label>Schedule</Label>
                <Select value={selectedPreset} onValueChange={handlePresetChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a schedule" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRESET_OPTIONS.map((preset) => (
                      <SelectItem key={preset.value} value={preset.value}>
                        {preset.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Current Times */}
              {customTimes.length > 0 && (
                <div className="space-y-2">
                  <Label>Reminder Times</Label>
                  <div className="flex flex-wrap gap-2">
                    {customTimes.map((time) => (
                      <div
                        key={time}
                        className="flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-sm"
                      >
                        <Clock className="h-3 w-3" />
                        {formatTime(time)}
                        <button
                          type="button"
                          onClick={() => removeTime(time)}
                          className="ml-1 hover:bg-blue-100 rounded-full p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add Custom Time */}
              {selectedPreset === 'CUSTOM' && (
                <div className="space-y-2">
                  <Label>Add Time</Label>
                  <div className="flex gap-2">
                    <Input
                      type="time"
                      value={newTime}
                      onChange={(e) => setNewTime(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addCustomTime}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </div>
              )}

              {/* Medication Details */}
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <div className="font-medium text-slate-700">{medication.name}</div>
                {medication.dosage && (
                  <div className="text-slate-500">Dosage: {medication.dosage}</div>
                )}
                {medication.instructions && (
                  <div className="text-slate-500 mt-1">{medication.instructions}</div>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving || (remindersEnabled && customTimes.length === 0)}
          >
            {saving ? (
              'Saving...'
            ) : (
              <>
                <Check className="h-4 w-4 mr-1" />
                Save Reminders
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MedicationReminderDialog;
